import React, { useState, useMemo, useEffect, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, ArrowUpDown, ArrowUp, ArrowDown, X, CheckCircle, ChevronLeft, ChevronRight, Eye, Plus, Check, Circle, CircleDot, Clock, FileCheck, Hourglass, ThumbsUp, ThumbsDown, Ban, ExternalLink, CheckSquare, Square, Edit2, Filter, Settings2, List, Linkedin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useRFQs } from '../../hooks/useRFQs';
import AddRFQModal from './AddRFQModal';
import { generateIndividualPost, generateMultiPost } from '../../utils/linkedinPostGenerator';
import { supabase } from '../../lib/supabaseClient';
import { formatDate } from '../../utils/dateFormatter';
import MobileTableCard from '../ui/mobile-table-card';
import ListContainer from '../ui/list-container';
import CalendarView from './CalendarView';

const STATUS_OPTIONS = [
  { value: 'not_invited', label: 'Not Invited' },
  { value: 'inclusion_requested', label: 'Inclusion Requested' },
  { value: 'request_declined', label: 'Request Declined' },
  { value: 'invited', label: 'Invited' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_outcome', label: 'Pending Outcome' },
  { value: 'successful', label: 'Successful' },
  { value: 'unsuccessful', label: 'Unsuccessful' },
  { value: 'not_interested', label: 'Not Interested' },
];

// Helper to load saved filter settings from localStorage
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem('rfqs_filter_settings');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading saved RFQ filters:', e);
  }
  return null;
};

const RFQsList = () => {
  const navigate = useNavigate();

  // Load saved filters on initial render
  const savedFilters = useMemo(() => loadSavedFilters(), []);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isPending, startTransition] = useTransition();
  const [selectedStatuses, setSelectedStatuses] = useState(savedFilters?.selectedStatuses || []);
  const [selectedClients, setSelectedClients] = useState(savedFilters?.selectedClients || []);
  const [selectedClearances, setSelectedClearances] = useState(savedFilters?.selectedClearances || []);
  const [clientSearch, setClientSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortField, setSortField] = useState(savedFilters?.sortField || 'created_at');
  const [sortDirection, setSortDirection] = useState(savedFilters?.sortDirection || 'desc'); // desc = latest first
  const [successMessage, setSuccessMessage] = useState(null);
  const [showClosed, setShowClosed] = useState(savedFilters?.showClosed ?? false); // Default to NOT showing closed RFQs
  const [showNotInterested, setShowNotInterested] = useState(savedFilters?.showNotInterested ?? false); // Default to NOT showing "not interested" RFQs
  const [viewMode, setViewMode] = useState(savedFilters?.viewMode || 'list'); // 'list' or 'calendar'

  // Bulk update state
  const [selectedRFQIds, setSelectedRFQIds] = useState([]);
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // LinkedIn post preview state
  const [linkedinPreview, setLinkedinPreview] = useState(null); // { content }
  const [copiedLinkedinPreview, setCopiedLinkedinPreview] = useState(false);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    ref_code: true,
    title: true,
    client: true,
    clearance: true,
    status: true,
    applicants: true,
    close_date: true,
    created_at: true
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Fetch all RFQs (no search parameter - we'll filter client-side)
  const { rfqs: allRfqs, loading, error, refetch } = useRFQs();

  // Debounce search term for smooth UX (non-blocking with useTransition)
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedSearchTerm(searchTerm);
      });
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Persist filter settings to localStorage when they change
  useEffect(() => {
    const filterSettings = {
      selectedStatuses,
      selectedClients,
      selectedClearances,
      sortField,
      sortDirection,
      showClosed,
      showNotInterested,
      viewMode,
    };
    localStorage.setItem('rfqs_filter_settings', JSON.stringify(filterSettings));
  }, [selectedStatuses, selectedClients, selectedClearances, sortField, sortDirection, showClosed, showNotInterested, viewMode]);

  const handleRFQCreated = (data, message) => {
    refetch();
    setSuccessMessage(message);
  };

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Toggle status selection
  const toggleStatus = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // Clear all status filters and reset to defaults
  const clearStatusFilters = () => {
    setSelectedStatuses([]);
    setSelectedClients([]);
    setSelectedClearances([]);
    setSortField('created_at');
    setSortDirection('desc');
    setShowClosed(false);
    setShowNotInterested(false);
    localStorage.removeItem('rfqs_filter_settings');
  };

  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Bulk selection handlers
  const toggleRFQSelection = (rfqId) => {
    setSelectedRFQIds((prev) =>
      prev.includes(rfqId)
        ? prev.filter((id) => id !== rfqId)
        : [...prev, rfqId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRFQIds.length === rfqs.length) {
      setSelectedRFQIds([]);
    } else {
      setSelectedRFQIds(rfqs.map((rfq) => rfq.id));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkUpdateStatus || selectedRFQIds.length === 0) return;

    setIsBulkUpdating(true);
    try {
      // Get auth token from Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required. Please log in again.');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/rfqs/bulk-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          rfq_ids: selectedRFQIds,
          status: bulkUpdateStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update RFQ statuses');
      }

      const data = await response.json();

      // Success
      setSuccessMessage(`Successfully updated ${data.updated} RFQ(s) to "${STATUS_OPTIONS.find(s => s.value === bulkUpdateStatus)?.label}"`);
      setSelectedRFQIds([]);
      setBulkUpdateStatus('');
      setShowBulkUpdateDialog(false);
      await refetch();
    } catch (error) {
      console.error('Error updating RFQ statuses:', error);
      alert('Failed to update RFQ statuses. Please try again.');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkLinkedInPost = async () => {
    if (selectedRFQIds.length === 0) return;

    const selectedRFQs = allRfqs.filter((rfq) => selectedRFQIds.includes(rfq.id));

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: liSettings } = await supabase
        .from('linkedin_integrations')
        .select('enabled, post_template, multi_post_template')
        .eq('user_id', user.id)
        .maybeSingle();

      const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;
      let content = '';

      if (selectedRFQs.length === 1) {
        const rfq = selectedRFQs[0];
        const template = liSettings?.post_template || `🎯 New Opportunity Available\n\n{{job_title}} | Ref: {{ref_code}}\n\n{{description}}\n\n📍 Location: {{locations}}\n🔒 Clearance: {{clearance}}\n💼 Work Arrangement: {{work_arrangement}}\n📅 Start Date: {{start_date}}\n\nApply now: {application_url}\n\n#hiring #opportunity #contract`;
        content = generateIndividualPost(template, rfq);
      } else {
        const template = liSettings?.multi_post_template || `🎯 New Opportunities Available\n\nRole 1: {{job_title.1}} | Ref: {{ref_code.1}}\n\n{{description.1}}\n\n---\n\nRole 2: {{job_title.2}} | Ref: {{ref_code.2}}\n\n{{description.2}}\n\n---\n\nExplore all open roles: ${SITE_URL}/jobs\n\n#hiring #opportunities #contract`;
        content = generateMultiPost(template, selectedRFQs);
      }

      setLinkedinPreview({ content });
    } catch (error) {
      console.error('Error generating LinkedIn post:', error);
      alert('Failed to generate LinkedIn post preview.');
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Unique clients derived from loaded RFQs for the client filter
  const uniqueClients = useMemo(() => {
    const names = [...new Set(allRfqs.map((r) => r.client?.name).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [allRfqs]);

  const CLEARANCE_OPTIONS = ['None', 'Baseline', 'NV1', 'NV2', 'TSPV'];

  // Filter and sort RFQs
  const filteredAndSortedRfqs = useMemo(() => {
    let filtered = allRfqs;

    // Filter by search term (debounced for performance)
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter((rfq) => {
        return (
          rfq.ref_code?.toLowerCase().includes(searchLower) ||
          rfq.title?.toLowerCase().includes(searchLower) ||
          rfq.client?.name?.toLowerCase().includes(searchLower) ||
          rfq.description?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter out closed RFQs if showClosed is false
    if (!showClosed) {
      const now = new Date();
      filtered = filtered.filter((rfq) => {
        if (!rfq.client_close_datetime) return true; // If no close date, show it
        return new Date(rfq.client_close_datetime) >= now; // Only show if close date is in the future or today
      });
    }

    // Filter out "not interested" RFQs if showNotInterested is false
    if (!showNotInterested) {
      filtered = filtered.filter((rfq) => rfq.status !== 'not_interested');
    }

    // Filter by selected statuses
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((rfq) => selectedStatuses.includes(rfq.status));
    }

    // Filter by selected clients
    if (selectedClients.length > 0) {
      filtered = filtered.filter((rfq) => selectedClients.includes(rfq.client?.name));
    }

    // Filter by selected clearance levels
    if (selectedClearances.length > 0) {
      filtered = filtered.filter((rfq) => {
        const level = rfq.clearance_required || 'None';
        return selectedClearances.includes(level);
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'ref_code':
          aValue = a.ref_code || '';
          bValue = b.ref_code || '';
          break;
        case 'title':
          aValue = a.title || '';
          bValue = b.title || '';
          break;
        case 'client':
          aValue = a.client?.name || '';
          bValue = b.client?.name || '';
          break;
        case 'clearance_required':
          aValue = a.clearance_required || '';
          bValue = b.clearance_required || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'created_at':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'client_close_datetime':
        default:
          aValue = a.client_close_datetime ? new Date(a.client_close_datetime).getTime() : 0;
          bValue = b.client_close_datetime ? new Date(b.client_close_datetime).getTime() : 0;
          break;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    return sorted;
  }, [allRfqs, debouncedSearchTerm, selectedStatuses, selectedClients, selectedClearances, sortField, sortDirection, showClosed, showNotInterested]);

  // Calculate pagination
  const totalItems = filteredAndSortedRfqs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatuses, itemsPerPage]);

  // Get current page items
  const rfqs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedRfqs.slice(startIndex, endIndex);
  }, [filteredAndSortedRfqs, currentPage, itemsPerPage]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
  };

  // Helper function to count active applicants (excluding withdrawn and unsuitable)
  const getActiveApplicantsCount = (rfq) => {
    if (!rfq.applications || !Array.isArray(rfq.applications)) return 0;
    return rfq.applications.filter(
      app => app.status !== 'withdrawn' && app.status !== 'unsuitable'
    ).length;
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 inline opacity-40" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1 inline" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1 inline" />
    );
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'inclusion_requested': 'bg-yellow-100 text-yellow-800',
      'invited': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-indigo-100 text-indigo-800',
      'submitted': 'bg-purple-100 text-purple-800',
      'pending_outcome': 'bg-orange-100 text-orange-800',
      'successful': 'bg-green-100 text-green-800',
      'unsuccessful': 'bg-red-100 text-red-800',
      'not_invited': 'bg-gray-100 text-gray-800',
      'not_interested': 'bg-slate-100 text-slate-800',
    };
    return colors[status] || 'bg-secondary text-secondary-foreground';
  };

  const getStatusIcon = (status) => {
    const iconProps = { className: "h-3 w-3 mr-1" };
    const icons = {
      'inclusion_requested': <Circle {...iconProps} />,
      'invited': <CircleDot {...iconProps} />,
      'in_progress': <Clock {...iconProps} />,
      'submitted': <FileCheck {...iconProps} />,
      'pending_outcome': <Hourglass {...iconProps} />,
      'successful': <ThumbsUp {...iconProps} />,
      'unsuccessful': <ThumbsDown {...iconProps} />,
      'not_invited': <Ban {...iconProps} />,
      'not_interested': <X {...iconProps} />,
    };
    return icons[status] || <Circle {...iconProps} />;
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Error loading RFQs: {error}</p>
      </div>
    );
  }

  return (
    <ListContainer>
      {/* Header */}
      <ListContainer.Header>
        <ListContainer.Title
          title="RFQs"
          subtitle="Manage requests for quotation"
        />
        <ListContainer.Actions>
          {selectedRFQIds.length > 0 && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowBulkUpdateDialog(true)}
                className="text-xs sm:text-sm"
              >
                <Edit2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                Update ({selectedRFQIds.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkLinkedInPost}
                className="text-xs sm:text-sm text-[#0077B5] border-[#0077B5] hover:bg-[#0077B5]/10"
              >
                <Linkedin className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                Post to LinkedIn ({selectedRFQIds.length})
              </Button>
            </>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs sm:text-sm">
                <Eye className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                View
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <div className="space-y-1">
                <div className="text-sm font-medium mb-2 px-2">Toggle columns</div>
                {Object.entries({
                  ref_code: 'Ref Code',
                  title: 'Title',
                  client: 'Client',
                  clearance: 'Clearance',
                  status: 'Status',
                  applicants: 'Applicants',
                  close_date: 'Close Date',
                  created_at: 'Created At'
                }).map(([key, label]) => (
                  <div
                    key={key}
                    className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                    onClick={() => toggleColumn(key)}
                  >
                    <div className="flex items-center justify-center w-4 h-4 border rounded">
                      {visibleColumns[key] && <Check className="h-3 w-3" />}
                    </div>
                    <span className="text-sm">{label}</span>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Button onClick={() => setShowAddModal(true)} size="sm" className="text-xs sm:text-sm">
            <Plus className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            Create
          </Button>
        </ListContainer.Actions>
      </ListContainer.Header>

      {/* Success Message */}
      {successMessage && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Filters and Table Card */}
      <Card>
        <CardHeader className="px-4 py-4">
          <div className="flex flex-col gap-4">
            {/* Search and Filter Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Filter Dropdowns */}
              <div className="flex flex-wrap gap-2">
                {/* Status Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <Filter className="mr-2 h-4 w-4" />
                      Status
                      {selectedStatuses.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedStatuses.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <div className="p-2 space-y-1">
                      {STATUS_OPTIONS.map((option) => (
                        <div
                          key={option.value}
                          className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                          onClick={() => toggleStatus(option.value)}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border rounded">
                            {selectedStatuses.includes(option.value) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <span className="text-sm">{option.label}</span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Client Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <Filter className="mr-2 h-4 w-4" />
                      Client
                      {selectedClients.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedClients.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <div className="p-2 border-b">
                      <Input
                        placeholder="Search clients..."
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                      />
                    </div>
                    <div className="p-2 space-y-1 max-h-56 overflow-y-auto">
                      {uniqueClients.filter((name) =>
                        name.toLowerCase().includes(clientSearch.toLowerCase())
                      ).length === 0 ? (
                        <p className="text-sm text-muted-foreground px-2 py-1.5">No clients found</p>
                      ) : uniqueClients
                          .filter((name) => name.toLowerCase().includes(clientSearch.toLowerCase()))
                          .map((name) => (
                          <div
                            key={name}
                            className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                            onClick={() => setSelectedClients((prev) =>
                              prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
                            )}
                          >
                            <div className="flex items-center justify-center w-4 h-4 border rounded shrink-0">
                              {selectedClients.includes(name) && <Check className="h-3 w-3" />}
                            </div>
                            <span className="text-sm truncate">{name}</span>
                          </div>
                        ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Clearance Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <Filter className="mr-2 h-4 w-4" />
                      Clearance
                      {selectedClearances.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedClearances.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-0" align="start">
                    <div className="p-2 space-y-1">
                      {CLEARANCE_OPTIONS.map((level) => (
                        <div
                          key={level}
                          className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                          onClick={() => setSelectedClearances((prev) =>
                            prev.includes(level) ? prev.filter((c) => c !== level) : [...prev, level]
                          )}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border rounded">
                            {selectedClearances.includes(level) && <Check className="h-3 w-3" />}
                          </div>
                          <span className="text-sm">{level}</span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Show Closed Toggle as Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      Show Closed
                      {showClosed && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          ON
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <div className="p-2 space-y-1">
                      <div
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => setShowClosed(!showClosed)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {showClosed && <Check className="h-3 w-3" />}
                        </div>
                        <span className="text-sm">Include Closed RFQs</span>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Show Not Interested Toggle as Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      Not Interested
                      {showNotInterested && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          ON
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <div className="p-2 space-y-1">
                      <div
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => setShowNotInterested(!showNotInterested)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {showNotInterested && <Check className="h-3 w-3" />}
                        </div>
                        <span className="text-sm">Include Not Interested</span>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Clear All Filters */}
                {(selectedStatuses.length > 0 || selectedClients.length > 0 || selectedClearances.length > 0 || showClosed || showNotInterested) && (
                  <Button
                    variant="ghost"
                    onClick={clearStatusFilters}
                    size="sm"
                    className="h-9"
                  >
                    Reset
                    <X className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Search */}
              <div className="relative sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search RFQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 border rounded-md p-1">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-7 px-2"
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className="h-7 px-2"
                >
                  <Calendar className="h-4 w-4" />
                </Button>
              </div>

              {/* View Columns Button */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <div className="space-y-1">
                    <div className="text-sm font-medium mb-2 px-2">Toggle columns</div>
                    {Object.entries({
                      ref_code: 'Ref Code',
                      title: 'Title',
                      client: 'Client',
                      clearance: 'Clearance',
                      status: 'Status',
                      applicants: 'Applicants',
                      close_date: 'Close Date',
                      created_at: 'Created At'
                    }).map(([key, label]) => (
                      <div
                        key={key}
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => toggleColumn(key)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {visibleColumns[key] && <Check className="h-3 w-3" />}
                        </div>
                        <span className="text-sm">{label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Active Filter Badges */}
            {(selectedStatuses.length > 0 || selectedClients.length > 0 || selectedClearances.length > 0 || searchTerm || showClosed || showNotInterested) && (
              <div className="flex flex-wrap gap-2">
                {searchTerm && (
                  <Badge variant="secondary" className="pl-2 pr-1">
                    Search: {searchTerm}
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => setSearchTerm('')}
                    />
                  </Badge>
                )}
                {selectedStatuses.map((status) => (
                  <Badge key={status} variant="secondary" className="pl-2 pr-1">
                    Status: {status.replace(/_/g, ' ')}
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => toggleStatus(status)}
                    />
                  </Badge>
                ))}
                {selectedClients.map((name) => (
                  <Badge key={name} variant="secondary" className="pl-2 pr-1">
                    Client: {name}
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => setSelectedClients((prev) => prev.filter((c) => c !== name))}
                    />
                  </Badge>
                ))}
                {selectedClearances.map((level) => (
                  <Badge key={level} variant="secondary" className="pl-2 pr-1">
                    Clearance: {level}
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => setSelectedClearances((prev) => prev.filter((c) => c !== level))}
                    />
                  </Badge>
                ))}
                {showClosed && (
                  <Badge variant="secondary" className="pl-2 pr-1">
                    Showing Closed
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => setShowClosed(false)}
                    />
                  </Badge>
                )}
                {showNotInterested && (
                  <Badge variant="secondary" className="pl-2 pr-1">
                    Showing Not Interested
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => setShowNotInterested(false)}
                    />
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {viewMode === 'list' ? (
            <>
          {/* Desktop: Table View */}
          <div className="hidden md:block overflow-x-auto px-4">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center justify-center w-full"
                    title={selectedRFQIds.length === rfqs.length ? 'Deselect all' : 'Select all'}
                  >
                    {selectedRFQIds.length === rfqs.length ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : selectedRFQIds.length > 0 ? (
                      <CheckSquare className="h-4 w-4 text-primary opacity-50" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </TableHead>
                {visibleColumns.ref_code && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('ref_code')}
                  >
                    Ref Code{getSortIcon('ref_code')}
                  </TableHead>
                )}
                {visibleColumns.title && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('title')}
                  >
                    Title{getSortIcon('title')}
                  </TableHead>
                )}
                {visibleColumns.client && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('client')}
                  >
                    Client{getSortIcon('client')}
                  </TableHead>
                )}
                {visibleColumns.clearance && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('clearance_required')}
                  >
                    Clearance{getSortIcon('clearance_required')}
                  </TableHead>
                )}
                {visibleColumns.status && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('status')}
                  >
                    Status{getSortIcon('status')}
                  </TableHead>
                )}
                {visibleColumns.applicants && (
                  <TableHead className="text-center">
                    Applicants
                  </TableHead>
                )}
                {visibleColumns.close_date && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('client_close_datetime')}
                  >
                    Close Date{getSortIcon('client_close_datetime')}
                  </TableHead>
                )}
                {visibleColumns.created_at && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('created_at')}
                  >
                    Created At{getSortIcon('created_at')}
                  </TableHead>
                )}
                <TableHead className="w-[100px]">View PD</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rfqs.map((rfq) => (
                <TableRow
                  key={rfq.id}
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => navigate(`/recruiter/rfqs/${rfq.id}`)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleRFQSelection(rfq.id)}
                      className="flex items-center justify-center w-full"
                      title={selectedRFQIds.includes(rfq.id) ? 'Deselect' : 'Select'}
                    >
                      {selectedRFQIds.includes(rfq.id) ? (
                        <CheckSquare className="h-4 w-4 text-primary" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  </TableCell>
                  {visibleColumns.ref_code && (
                    <TableCell className="font-medium">{rfq.ref_code}</TableCell>
                  )}
                  {visibleColumns.title && (
                    <TableCell>{rfq.title}</TableCell>
                  )}
                  {visibleColumns.client && (
                    <TableCell>{rfq.client?.name || 'N/A'}</TableCell>
                  )}
                  {visibleColumns.clearance && (
                    <TableCell>
                      {rfq.clearance_required ? (
                        <Badge className="bg-blue-100 text-blue-800">
                          {rfq.clearance_required}
                        </Badge>
                      ) : (
                        'None'
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell>
                      <Badge className={`${getStatusBadgeColor(rfq.status)} flex items-center w-fit`}>
                        {getStatusIcon(rfq.status)}
                        {rfq.status.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.applicants && (
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="font-medium">
                        {getActiveApplicantsCount(rfq)}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.close_date && (
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(rfq.client_close_datetime, { style: 'short', fallback: 'N/A' })}
                    </TableCell>
                  )}
                  {visibleColumns.created_at && (
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(rfq.created_at, { style: 'short', fallback: 'N/A' })}
                    </TableCell>
                  )}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = `/job/${rfq.id}`;
                        window.open(url, '_blank');
                      }}
                      title="View Public Display - opens in new tab"
                      className="h-8 w-8 p-0"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          {/* Mobile: Card View */}
          <div className="md:hidden space-y-3 p-2 w-full max-w-full overflow-hidden">
            {rfqs.map((rfq) => (
              <div key={rfq.id} className="relative">
                {/* Checkbox in top-left corner */}
                <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => toggleRFQSelection(rfq.id)}
                    className="flex items-center justify-center p-1 bg-background rounded border"
                    title={selectedRFQIds.includes(rfq.id) ? 'Deselect' : 'Select'}
                  >
                    {selectedRFQIds.includes(rfq.id) ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </div>

                <MobileTableCard
                  item={rfq}
                  primaryField="title"
                  secondaryField="ref_code"
                  statusField="status"
                  getStatusVariant={getStatusBadgeColor}
                  className="pl-10"
                  fields={[
                    {
                      label: 'Client',
                      key: 'client',
                      render: (client) => client?.name || 'N/A',
                      showOnMobile: true,
                    },
                    {
                      label: 'Clearance',
                      key: 'clearance_required',
                      render: (clearance) =>
                        clearance ? (
                          <Badge className="bg-blue-100 text-blue-800 text-xs">{clearance}</Badge>
                        ) : (
                          'None'
                        ),
                      showOnMobile: true,
                    },
                    {
                      label: 'Applicants',
                      key: 'applications',
                      render: () => (
                        <Badge variant="secondary" className="text-xs">
                          {getActiveApplicantsCount(rfq)}
                        </Badge>
                      ),
                      showOnMobile: true,
                    },
                    {
                      label: 'Close Date',
                      key: 'client_close_datetime',
                      render: (date) => formatDate(date, { style: 'short', fallback: 'N/A' }),
                      showOnMobile: true,
                    },
                  ]}
                  actions={
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs"
                        onClick={() => navigate(`/recruiter/rfqs/${rfq.id}`)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = `/job/${rfq.id}`;
                          window.open(url, '_blank');
                        }}
                        title="View Public Display"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </>
                  }
                  onClick={() => navigate(`/recruiter/rfqs/${rfq.id}`)}
                />
              </div>
            ))}
          </div>

          {rfqs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No RFQs found</p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-primary hover:text-primary/80 text-sm mt-2"
                >
                  Clear search
                </button>
              )}
            </div>
          )}

          {/* Pagination Controls */}
          {totalItems > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t pt-4 px-4 sm:px-0">
              {/* Items per page selector */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border rounded px-2 py-1 bg-background"
                >
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-muted-foreground">per page</span>
              </div>

              {/* Page info */}
              <div className="text-sm text-muted-foreground">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
              </div>

              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="min-w-[2.5rem]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
            </>
          ) : (
            // Calendar View
            <div className="p-4">
              <CalendarView rfqs={filteredAndSortedRfqs} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add RFQ Modal */}
      <AddRFQModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        onSuccess={handleRFQCreated}
      />

      {/* LinkedIn Post Preview Dialog */}
      <Dialog open={!!linkedinPreview} onOpenChange={() => { setLinkedinPreview(null); setCopiedLinkedinPreview(false); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Linkedin className="h-5 w-5 text-[#0077B5]" />
              LinkedIn Post Preview
            </DialogTitle>
            <DialogDescription>
              {selectedRFQIds.length > 1
                ? `Generated using the Multiple Role template for ${selectedRFQIds.length} roles.`
                : 'Generated using the Individual Role template.'}{' '}
              Copy and post manually to LinkedIn.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-muted rounded-md p-4 text-sm whitespace-pre-wrap font-mono max-h-80 overflow-y-auto border">
              {linkedinPreview?.content}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setLinkedinPreview(null); setCopiedLinkedinPreview(false); }}>
              Done
            </Button>
            <Button
              onClick={() => {
                if (linkedinPreview?.content) {
                  navigator.clipboard.writeText(linkedinPreview.content).then(() => {
                    setCopiedLinkedinPreview(true);
                    setTimeout(() => setCopiedLinkedinPreview(false), 2000);
                  });
                }
              }}
              className="gap-2"
            >
              {copiedLinkedinPreview ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                'Copy Post'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Bulk Status Update</DialogTitle>
            <DialogDescription>
              Update the status for {selectedRFQIds.length} selected RFQ{selectedRFQIds.length !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="status" className="text-sm font-medium">
                New Status
              </label>
              <select
                id="status"
                value={bulkUpdateStatus}
                onChange={(e) => setBulkUpdateStatus(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select a status...</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowBulkUpdateDialog(false);
                setBulkUpdateStatus('');
              }}
              disabled={isBulkUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkStatusUpdate}
              disabled={!bulkUpdateStatus || isBulkUpdating}
            >
              {isBulkUpdating ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListContainer>
  );
};

export default RFQsList;
